import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';
import { getOutreachTracker } from '../../utils/api';
import OfficeDrawer from '../components/OfficeDrawer';
import { OUTREACH_STATUSES, PAGE_SIZES, PRIORITIES, PRIORITY_LABELS, PRIORITY_PILL, STATUS_LABELS, STATUS_PILL, dueTone, fmtDateOnly, loadError, todayYmd } from '../utils';

const BUCKETS = [['overdue', 'Overdue', 'ops-bucket--red'], ['dueToday', 'Due today', 'ops-bucket--amber'], ['upcoming', 'Upcoming', ''], ['noFollowUp', 'No follow-up set', '']];

function inBucket(o, bucket, t) {
  const d = o.nextFollowUpDate;
  const closed = ['declined', 'do_not_contact'].includes(o.outreachStatus);
  switch (bucket) {
    case 'overdue': return Boolean(d) && d < t && !closed;
    case 'dueToday': return d === t;
    case 'due': return Boolean(d) && d <= t && !closed;
    case 'upcoming': return Boolean(d) && d > t;
    case 'noFollowUp': return !d && !closed;
    default: return true;
  }
}

const getter = (o, col) => {
  switch (col) {
    case 'name': return o.name || '';
    case 'priority': return PRIORITIES.indexOf(o.priority);
    case 'status': return OUTREACH_STATUSES.indexOf(o.outreachStatus);
    case 'assignedTo': return o.assignedTo || '';
    case 'lastContactedAt': return o.lastContactedAt || '';
    case 'nextFollowUpDate': return o.nextFollowUpDate || '9999';
    default: return o[col] || '';
  }
};

export default function TrackerPage() {
  const [params, setParams] = useSearchParams();
  const [showToast, toastNode] = useToast();
  const status = params.get('status') || '';
  const assignedTo = params.get('assignedTo') || '';
  const bucket = params.get('bucket') || '';
  const q = params.get('q') || '';
  const selected = params.get('office');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const { sort, onSort, apply } = useSort('nextFollowUpDate', 'asc');

  const setParam = useCallback((patch) => {
    setParams(prev => { const n = new URLSearchParams(prev); Object.entries(patch).forEach(([k, v]) => { if (!v) n.delete(k); else n.set(k, v); }); return n; }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    let cancelled = false;
    getOutreachTracker({ status, assignedTo, q }).then(r => { if (!cancelled) { setData(r); setError(''); } })
      .catch(e => { if (!cancelled) { setError(loadError(e, 'Could not load the outreach tracker')); setData({ items: [], buckets: {}, byStatus: {}, assignees: [] }); } });
    return () => { cancelled = true; };
  }, [status, assignedTo, q, tick]);

  const t = todayYmd();
  const rows = useMemo(() => apply((data?.items || []).filter(o => inBucket(o, bucket, t)), getter), [data, bucket, t, apply]);
  const { pageRows, pagerProps } = usePager(rows, [status, assignedTo, bucket, q, sort.col, sort.dir], 25);
  const total = Object.values(data?.byStatus || {}).reduce((a, b) => a + b, 0);

  return (
    <>
      <PageHeader title="Outreach tracker" subtitle="Offices the team is actively working: who owns each one, when they were last contacted and what comes next." meta={data ? `${rows.length} of ${data.items.length} shown` : ''} />
      {error && <div className="ops-error">{error}</div>}

      <div className="ops-buckets" style={{ marginBottom: 14 }}>
        <button type="button" className={`ops-bucket${!bucket ? ' is-active' : ''}`} onClick={() => setParam({ bucket: '' })}><strong>{data?.items?.length ?? 0}</strong> All in workflow</button>
        {BUCKETS.map(([k, l, cls]) => <button key={k} type="button" className={`ops-bucket ${cls}${bucket === k ? ' is-active' : ''}`} onClick={() => setParam({ bucket: k })}><strong>{data?.buckets?.[k] ?? 0}</strong> {l}</button>)}
      </div>

      <Panel flush>
        <div className="ops-tabs" role="tablist" style={{ padding: '0 18px' }}>
          <button type="button" role="tab" aria-selected={!status} className={!status ? 'is-active' : ''} onClick={() => setParam({ status: '' })}>All <span className="count">{total}</span></button>
          {OUTREACH_STATUSES.filter(s => s !== 'not_contacted').map(s => (
            <button key={s} type="button" role="tab" aria-selected={status === s} className={status === s ? 'is-active' : ''} onClick={() => setParam({ status: s })}>{STATUS_LABELS[s]} <span className="count">{data?.byStatus?.[s] ?? 0}</span></button>
          ))}
        </div>
        <div className="ops-filters" style={{ padding: '12px 18px', borderBottom: '1px solid var(--line)' }}>
          <input className="ops-search" type="search" placeholder="Search offices" defaultValue={q} onChange={e => { const v = e.target.value; setTimeout(() => setParam({ q: v }), 300); }} aria-label="Search" />
          <select className="ops-select ops-select--sm" value={assignedTo} onChange={e => setParam({ assignedTo: e.target.value })} aria-label="Assigned staff"><option value="">All staff</option>{(data?.assignees || []).map(a => <option key={a} value={a}>{a}</option>)}</select>
        </div>
        {!data ? <div style={{ padding: 18 }}><Skeleton rows={6} /></div> : !rows.length ? <Empty title={data.items.length ? 'Nothing matches these filters' : 'No offices are in the outreach workflow yet. Update an office in the directory to start tracking it.'} /> : (
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <SortTh col="name" label="Office" sort={sort} onSort={onSort} />
                  <SortTh col="priority" label="Priority" sort={sort} onSort={onSort} />
                  <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                  <SortTh col="assignedTo" label="Assigned" sort={sort} onSort={onSort} />
                  <SortTh col="lastContactedAt" label="Last contacted" sort={sort} onSort={onSort} />
                  <SortTh col="nextFollowUpDate" label="Next follow-up" sort={sort} onSort={onSort} />
                  <th>Contact and outcome</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(o => (
                  <tr key={o.officeId} className={`is-clickable${selected === o.officeId ? ' is-selected' : ''}`} onClick={() => setParam({ office: o.officeId })}>
                    <td><div className="ops-cell-main">{o.name}</div><div className="ops-cell-sub">{[o.areaLabel || o.city, o.phoneDisplay || o.phone].filter(Boolean).join(' · ')}</div></td>
                    <td><StatusPill status={PRIORITY_PILL[o.priority] || 'neutral'} label={PRIORITY_LABELS[o.priority] || o.priority} /></td>
                    <td><StatusPill status={STATUS_PILL[o.outreachStatus] || 'neutral'} label={STATUS_LABELS[o.outreachStatus] || 'Not contacted'} /></td>
                    <td>{o.assignedTo || <span className="muted">Unassigned</span>}</td>
                    <td>{o.lastContactedAt ? fmtDateOnly(String(o.lastContactedAt).slice(0, 10)) : <span className="muted">Never</span>}</td>
                    <td className={dueTone(o.nextFollowUpDate) === 'neg' ? 'neg' : ''}>{o.nextFollowUpDate ? fmtDateOnly(o.nextFollowUpDate) : <span className="muted">None</span>}{o.nextFollowUpDate === t ? <div className="ops-cell-sub">Today</div> : null}</td>
                    <td><div className="ops-cell-main">{o.contactPerson || ''}</div><div className="ops-cell-sub ops-truncate" style={{ maxWidth: 280 }}>{o.contactOutcome || ''}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows.length > PAGE_SIZES[0] && <div style={{ padding: '10px 18px', borderTop: '1px solid var(--line)' }}><Pager {...pagerProps} sizes={PAGE_SIZES} label="offices" /></div>}
      </Panel>

      {selected && <OfficeDrawer officeId={selected} onClose={() => setParam({ office: '' })} onChanged={() => setTick(x => x + 1)} showToast={showToast} />}
      {toastNode}
    </>
  );
}
